/**
 * Servicio de API
 * Cliente centralizado para comunicación con el backend
 */

import axios from 'axios';

// Configurar base URL del API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// GET públicos: validación de query PSE (debajo, `publicApi`)
function isPseReturnStatusQueryResolvable(qNormalized) {
  const s =
    typeof qNormalized === 'string' && qNormalized !== ''
      ? qNormalized.startsWith('?')
        ? qNormalized
        : `?${qNormalized}`
      : '';
  if (s === '' || s === '?') return false;
  const p = new URLSearchParams(s);
  const txnKeys = ['id', 'transaction_id', 'transactionId', 'wompi_id', 'wompiId'];
  if (
    txnKeys.some((k) => {
      const v = p.get(k);
      return v != null && String(v).trim() !== '';
    })
  ) {
    return true;
  }
  const refKeys = ['ref', 'reference', 'orderNumber', 'order_reference', 'reference_order'];
  const refOk = refKeys.some((k) => {
    const v = p.get(k);
    return v != null && String(v).trim() !== '';
  });
  const oid = p.get('order_id');
  const orderOk = oid != null && String(oid).trim() !== '';
  if (refOk || orderOk) return true;
  const qs = normalizePseQs(qNormalized);
  if (qs === '' || qs === '?') return false;
  const p2 = new URLSearchParams(qs);
  const keys = Array.from(new Set(Array.from(p2.keys())));
  const pseVal = p2.get('pse');
  return (
    (pseVal === '1' || pseVal === 'true') &&
    keys.length > 0 &&
    keys.every((k) => k === 'pse')
  );
}

function normalizePseQs(qNormalized) {
  if (typeof qNormalized !== 'string' || qNormalized === '') return '';
  return qNormalized.startsWith('?') ? qNormalized : `?${qNormalized}`;
}

/**
 * Wompi a veces concatena env=undefined (u otros placeholders) en el redirect del comercio.
 * Los quitamos antes de llamar a pse-return-status y antes de sanear la URL en el SPA.
 */
export function sanitizePseReturnQueryString(queryStringRaw) {
  const s =
    typeof queryStringRaw === 'string' && queryStringRaw.trim() !== ''
      ? queryStringRaw.trim().startsWith('?')
        ? queryStringRaw.trim()
        : `?${queryStringRaw.trim()}`
      : '?';
  if (s === '?' || s === '') return '?';
  const p = new URLSearchParams(s);
  const next = new URLSearchParams();
  for (const [key, rawVal] of p.entries()) {
    const val = String(rawVal).trim();
    if (
      val === '' ||
      /^undefined$/i.test(val) ||
      /^null$/i.test(val)
    ) {
      continue;
    }
    next.append(key, String(rawVal).trim());
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '?';
}

/** Instancia axios sin interceptors (GET públicos; credenciales para cookie PSE tras create). */
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('javiland_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // No redirigir si el 401 es del intento de login (credenciales incorrectas)
      const skipLoginRedirect =
        url.includes('/auth/admin-login') ||
        url.includes('/auth/member') ||
        url.includes('/orders/member-recent') ||
        url.includes('/payments/pse-financial-institutions') ||
        url.includes('/payments/public-key') ||
        url.includes('/payments/merchant') ||
        url.includes('/payments/pse-return-status');
      if (!skipLoginRedirect) {
        localStorage.removeItem('javiland_token');
        localStorage.removeItem('javiland_user');
        const adminApi = /\/admin\//.test(url);
        window.location.href = adminApi ? '/admin-login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===== SERVICIOS DE IMPUESTOS =====

export const taxService = {
  /**
   * Calcular impuestos de orden
   * @param {Array} items - Items de la orden
   */
  calculateOrderTaxes: async (items) => {
    try {
      const response = await api.post('/taxes/calculate', { items });
      return response.data;
    } catch (error) {
      console.error('Error calculando impuestos:', error);
      throw error;
    }
  },

  /**
   * Calcular precio con IVA incluido
   * @param {Number} basePrice - Precio base
   * @param {String} taxCategory - Categoría fiscal
   */
  calculatePriceWithTax: async (basePrice, taxCategory = 'standard') => {
    try {
      const response = await api.post('/taxes/calculate-with-tax', {
        basePrice,
        taxCategory
      });
      return response.data;
    } catch (error) {
      console.error('Error calculando precio con IVA:', error);
      throw error;
    }
  },

  /**
   * Obtener categorías fiscales disponibles
   */
  getCategories: async () => {
    try {
      const response = await api.get('/taxes/categories');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo categorías:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS DE ENVÍO =====

export const shippingService = {
  /**
   * Calcular costo de envío
   * @param {Object} addressData - Datos de dirección
   * @param {Number} weight - Peso en gramos
   * @param {String} packageType - Tipo de paquete
   * @param {Array} items - Items de la orden
   */
  calculateShipping: async (addressData, weight, packageType, items) => {
    try {
      const response = await api.post('/shipping/calculate', {
        city: addressData.city,
        department: addressData.department,
        zipCode: addressData.zipCode,
        fullAddress: addressData.fullAddress || addressData.address,
        municipalityDane: addressData.municipalityDane,
        country: addressData.country || 'Colombia',
        subtotal: addressData.subtotal || 0,
        weight,
        packageType,
        items
      });
      return response.data;
    } catch (error) {
      console.error('Error calculando envío:', error);
      throw error;
    }
  },

  /**
   * Obtener ciudades principales con envío gratis
   */
  getMainCities: async () => {
    try {
      const response = await api.get('/shipping/main-cities');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo ciudades:', error);
      throw error;
    }
  },

  /**
   * Validar dirección
   * @param {Object} address - Dirección a validar
   */
  validateAddress: async (address) => {
    try {
      const response = await api.post('/shipping/validate-address', address);
      return response.data;
    } catch (error) {
      console.error('Error validando dirección:', error);
      throw error;
    }
  },

  /**
   * Obtener todos los departamentos de Colombia
   */
  getDepartments: async () => {
    try {
      const response = await api.get('/shipping/departments');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo departamentos:', error);
      throw error;
    }
  },

  /**
   * Obtener ciudades por departamento
   * @param {String} department - Nombre del departamento
   */
  getCitiesByDepartment: async (department) => {
    try {
      const response = await api.get(`/shipping/cities/${encodeURIComponent(department)}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo ciudades:', error);
      throw error;
    }
  },

  /**
   * Municipios con código DANE (solo nombre visible en UI).
   */
  getMunicipalitiesByDepartment: async (department) => {
    try {
      const response = await api.get('/shipping/municipalities', {
        params: { department }
      });
      return response.data;
    } catch (error) {
      console.error('Error obteniendo municipios:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS DE ÓRDENES =====

export const orderService = {
  /**
   * Crear una nueva orden
   * @param {Object} orderData - Datos completos de la orden
   */
  createOrder: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creando orden:', error);
      throw error;
    }
  },

  /**
   * Abandonar cobro pendiente (Nequi / sin pagar): devuelve stock y cancela el pedido. Requiere el mismo email del checkout.
   */
  abandonPendingOrder: async ({ orderId, customerEmail }) => {
    const response = await api.post('/orders/abandon-pending', {
      orderId,
      customerEmail
    });
    return response.data;
  },

  /**
   * Obtener el estado de una orden
   * @param {Number} orderId - ID de la orden
   */
  getOrderStatus: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo orden:', error);
      throw error;
    }
  },

  /**
   * Obtener una orden por ID (alias de getOrderStatus)
   * @param {Number} orderId
   */
  getOrderById: async (orderId) => {
    return orderService.getOrderStatus(orderId);
  },

  /**
   * Obtener órdenes del usuario autenticado
   */
  getMyOrders: async () => {
    try {
      const response = await api.get('/orders/my-orders');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo mis órdenes:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS DE CUPONES =====

export const couponService = {
  /**
   * Validar un cupón por código
   * @param {String} code - Código cupón
   */
  validateCoupon: async (code) => {
    try {
      const safeCode = encodeURIComponent(String(code || '').trim());
      const response = await api.get(`/coupons/validate/${safeCode}`);
      return response.data;
    } catch (error) {
      console.error('Error validando cupón:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS BIENESTAR (PR1b) =====

export const bienestarService = {
  getCatalog: async () => {
    const response = await api.get('/bienestar/catalog');
    return response.data;
  },
  requestWelcomeCoupon: async (email, service) => {
    const response = await api.post('/bienestar/welcome-coupon', { email, service });
    return response.data;
  },
  confirmBooking: async (payload) => {
    const response = await api.post('/bienestar/confirm-booking', payload);
    return response.data;
  },
  getAvailability: async (service, from, to, opts = {}) => {
    const params = new URLSearchParams({ service });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (opts.bookingRef) params.set('bookingRef', opts.bookingRef);
    if (opts.email) params.set('email', opts.email);
    const response = await api.get(`/bienestar/availability?${params.toString()}`);
    return response.data;
  },
  scheduleBooking: async (payload) => {
    const response = await api.post('/bienestar/schedule', payload);
    return response.data;
  },
  getAgenda: async (limit = 20, service = null) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (service) params.set('service', service);
    const response = await api.get(`/bienestar/agenda?${params.toString()}`);
    return response.data;
  },
  getBooking: async (bookingRef, email) => {
    const params = new URLSearchParams({ ref: bookingRef, email });
    const response = await api.get(`/bienestar/booking?${params.toString()}`);
    return response.data;
  },
  rescheduleBooking: async (payload) => {
    const response = await api.post('/bienestar/reschedule', payload);
    return response.data;
  },
};

// ===== SERVICIOS DE PAGOS (MERCADO PAGO) =====

export const paymentService = {
  /**
   * Obtener public key de Mercado Pago
   */
  getPublicKey: async () => {
    try {
      const response = await api.get('/payments/public-key');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo public key:', error);
      throw error;
    }
  },

  /**
   * Wompi: obtener merchant info (acceptance tokens + permalinks)
   */
  getMerchant: async () => {
    try {
      const response = await api.get('/payments/merchant');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo merchant Wompi:', error);
      throw error;
    }
  },

  /**
   * Wompi: listado de bancos PSE (sandbox/prod según API)
   */
  getPseFinancialInstitutions: async () => {
    try {
      const response = await api.get('/payments/pse-financial-institutions');
      return response.data;
    } catch (error) {
      console.error('Error listando bancos PSE:', error);
      throw error;
    }
  },

  /**
   * Crear pago
   * @param {Object} paymentData - Datos del pago
   */
  createPayment: async (paymentData) => {
    try {
      console.log('📤 Enviando datos de pago:', {
        hasToken: !!paymentData.token,
        orderId: paymentData.orderId,
        paymentMethodId: paymentData.paymentMethodId,
        installments: paymentData.installments,
        hasPayer: !!paymentData.payer
      });
      const response = await api.post('/payments/create', paymentData, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('❌ Error creando pago:', error);
      console.error('📥 Respuesta del servidor:', error.response?.data);
      // Re-lanzar el error con más información
      if (error.response?.data) {
        error.serverError = error.response.data;
      }
      throw error;
    }
  },

  /**
   * Obtener estado de pago de orden
   * @param {Number} orderId - ID de la orden
   */
  getPaymentStatus: async (orderId) => {
    try {
      const response = await api.get(`/payments/status/${orderId}`, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estado de pago:', error);
      throw error;
    }
  },

  /**
   * Retorno PSE desde Wompi: estado de orden/pago usando query (?id= / ref= …)
   * @param {string} queryString — Sin o con "?", p. ej. window.location.search
   */
  getPseReturnStatus: async (queryString = '') => {
    const rawNormalized =
      typeof queryString === 'string'
        ? queryString.startsWith('?')
          ? queryString
          : queryString !== ''
            ? `?${queryString}`
            : ''
        : '';
    const q = sanitizePseReturnQueryString(rawNormalized || '?');
    const relativePath = `/payments/pse-return-status${q}`;
    if (!isPseReturnStatusQueryResolvable(q)) {
      console.warn('PSE status: omitido GET (solo pse sin id/ref/order_id):', relativePath);
      return { skipped: true, reason: 'incomplete_query' };
    }
    const base = String(publicApi.defaults.baseURL ?? '').replace(/\/+$/, '');
    console.log('PSE status URL:', base ? `${base}${relativePath}` : relativePath);
    try {
      const response = await publicApi.get(relativePath);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estado retorno PSE:', error);
      throw error;
    }
  },

  /**
   * Obtener cuotas disponibles para un método de pago
   * @param {String} paymentMethodId - ID del método de pago (visa, master, etc.)
   * @param {String} bin - Primeros 6 dígitos de la tarjeta
   * @param {Number} amount - Monto de la transacción
   * @param {String} issuerId - ID del emisor (opcional)
   */
  getInstallments: async (paymentMethodId, bin, amount, issuerId = null) => {
    try {
      const params = new URLSearchParams({
        payment_method_id: paymentMethodId,
        bin: bin.substring(0, 6),
        amount: amount.toString()
      });
      
      if (issuerId) {
        params.append('issuer_id', issuerId);
      }

      const response = await api.get(`/payments/installments?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo cuotas:', error);
      throw error;
    }
  },

  /**
   * Obtener métodos de pago disponibles
   */
  getPaymentMethods: async () => {
    try {
      const response = await api.get('/payments/methods');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo métodos de pago:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS DE PRODUCTOS =====

export const productService = {
  /**
   * Obtener todos los productos
   */
  /** @param {Record<string,string|number|undefined>} [params] estado: all|active|inactive|upcoming|soldout, limit, page, category */
  getAllProducts: async (params = {}) => {
    try {
      const response = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error obteniendo productos:', error);
      throw error;
    }
  },

  /**
   * Verificar stock disponible de un producto
   * @param {Number} productId - ID del producto
   */
  checkStock: async (productId) => {
    try {
      const response = await api.get(`/products/${productId}/stock`);
      return response.data;
    } catch (error) {
      console.error('Error verificando stock:', error);
      throw error;
    }
  },

  /**
   * Crear producto (admin)
   * @param {Object} productData - Datos del producto
   */
  createProduct: async (productData) => {
    try {
      const response = await api.post('/products', productData);
      return response.data;
    } catch (error) {
      console.error('Error creando producto:', error);
      throw error;
    }
  },

  /**
   * Importación masiva desde CSV (UTF-8). Admin: JWT.
   * @param {File|Blob} file — CSV con encabezados de plantilla_productos_javiland.csv
   * @param {boolean} dryRun — si true, POST con ?dry_run=1 (solo valida lista)
   */
  bulkImportCsv: async (file, dryRun = false) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const q = dryRun ? '?dry_run=1' : '';
      const response = await api.post(`/products/bulk-import${q}`, form, {
        timeout: 120000
      });
      return response.data;
    } catch (error) {
      console.error('Error importando CSV:', error);
      throw error;
    }
  },

  /**
   * Actualizar producto (admin)
   * @param {Number} productId - ID del producto
   * @param {Object} productData - Datos actualizados
   */
  updateProduct: async (productId, productData) => {
    try {
      const response = await api.put(`/products/${productId}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error actualizando producto:', error);
      throw error;
    }
  },

  /**
   * Eliminar producto (admin)
   * @param {Number} productId - ID del producto
   */
  deleteProduct: async (productId) => {
    try {
      const response = await api.delete(`/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error eliminando producto:', error);
      throw error;
    }
  }
};

// ===== SERVICIOS DE FACTURAS =====

export const invoiceService = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/invoices${qs ? '?' + qs : ''}`);
    return response.data;
  },
  getPendingReview: async () => {
    const response = await api.get('/invoices/pending-review');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },
  approve: async (id) => {
    const response = await api.post(`/invoices/${id}/approve`);
    return response.data;
  },
  reject: async (id, reason = '') => {
    const response = await api.post(`/invoices/${id}/reject`, { reason });
    return response.data;
  },
  /** Reenvío a Numbers para facturas aprobadas sin id o con error previo */
  resendToNumbers: async (id) => {
    const response = await api.post(`/invoices/${id}/resend-to-numbers`);
    return response.data;
  }
};

// ===== SERVICIOS ADMIN (DASHBOARD KPIs) =====

export const adminService = {
  /**
   * Login admin contra API PHP; devuelve JWT. Credenciales = javiland_admin_users.
   */
  adminLogin: async (email, password) => {
    const response = await api.post('/auth/admin-login', { email, password });
    return response.data;
  },
  getKpis: async (period = '7dias') => {
    const response = await api.get(`/admin/kpis?period=${encodeURIComponent(period)}`);
    return response.data;
  },
  /** Conteos badges + resumen operativo (órdenes por estado, colas) */
  getOperationsSummary: async () => {
    const response = await api.get('/admin/operations-summary');
    return response.data;
  },
  getSearchConsoleSummary: async (days = 28) => {
    const response = await api.get(`/admin/search-console-summary?days=${days}`);
    return response.data;
  },
  getGa4Summary: async (days = 28) => {
    const response = await api.get(`/admin/ga4-summary?days=${days}`);
    return response.data;
  },
  getMembers: async () => {
    const response = await api.get('/admin/members');
    return response.data;
  },
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },
  updateUser: async (userId, userData) => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  getSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },
  updateSettings: async (settings) => {
    const response = await api.put('/admin/settings', settings);
    return response.data;
  },
  getLogs: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/logs${qs ? '?' + qs : ''}`);
    return response.data;
  },
  getTestimonials: async (filter = 'todos') => {
    const response = await api.get(`/admin/testimonials?filter=${filter}`);
    return response.data;
  },
  updateTestimonialStatus: async (id, status) => {
    const response = await api.put(`/admin/testimonials/${id}`, { status });
    return response.data;
  },
  createTestimonial: async (data) => {
    const response = await api.post('/admin/testimonials', data);
    return response.data;
  },
  getOrders: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/orders${qs ? '?' + qs : ''}`);
    return response.data;
  },
  /** Pedidos pagados en cola de empaque */
  getPackagingQueue: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/packaging-queue${qs ? '?' + qs : ''}`);
    return response.data;
  },
  /** Cambiar estado: { status, tracking_number? } */
  updateOrderStatus: async (orderId, data) => {
    const response = await api.put(`/admin/orders/${orderId}`, data);
    return response.data;
  },
  /** Pago pending en BD pero cobro real (p. ej. webhook ignorado) → paid + awaiting_packaging + factura */
  markOrderAsPaid: async (orderId) => {
    const response = await api.post(`/admin/orders/${orderId}/mark-paid`);
    return response.data;
  },
  getServices: async () => {
    const response = await api.get('/admin/services');
    return response.data;
  },
  createService: async (data) => {
    const response = await api.post('/admin/services', data);
    return response.data;
  },
  updateService: async (id, data) => {
    const response = await api.put(`/admin/services/${id}`, data);
    return response.data;
  },
  listQuotations: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/quotations${qs ? '?' + qs : ''}`);
    return response.data;
  },
  getQuotation: async (id) => {
    const response = await api.get(`/admin/quotations/${id}`);
    return response.data;
  },
  createQuotation: async (data) => {
    const response = await api.post('/admin/quotations', data);
    return response.data;
  },
  updateQuotation: async (id, data) => {
    const response = await api.put(`/admin/quotations/${id}`, data);
    return response.data;
  },
  /** Devuelve Blob PDF */
  downloadQuotationPdfBlob: async (id) => {
    const response = await api.get(`/admin/quotations/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },
  sendQuotation: async (id) => {
    const response = await api.post(`/admin/quotations/${id}/send`);
    return response.data;
  },
  newQuotationVersion: async (id) => {
    const response = await api.post(`/admin/quotations/${id}/version`);
    return response.data;
  },
  createQuotationUploadToken: async (id) => {
    const response = await api.post(`/admin/quotations/${id}/upload-token`);
    return response.data;
  },
  deleteQuotationAttachment: async (quotationId, attachmentId) => {
    const response = await api.delete(`/admin/quotations/${quotationId}/attachments/${attachmentId}`);
    return response.data;
  },
  getBienestarBookings: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/bienestar-bookings${qs ? '?' + qs : ''}`);
    return response.data;
  },
  updateBienestarBooking: async (id, data) => {
    const response = await api.put(`/admin/bienestar-bookings/${id}`, data);
    return response.data;
  },
};

export const returnRequestService = {
  submit: async (payload) => {
    const response = await api.post('/return-requests', {
      orderNumber: payload.orderNumber,
      fullName: payload.fullName,
      email: payload.email,
      phone: payload.phone,
      city: payload.city,
      reason: payload.reason,
      productsText: payload.productsText,
      issueDescription: payload.issueDescription,
      preferredResolution: payload.preferredResolution,
    });
    return response.data;
  },
};
